import "./Container.scss";
import "./forms/forms.scss";

import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from "react-router";
import PropTypes from "prop-types";
import { FILTERABLE, PAGE, TABS_TYPE, GRIDSIZE } from "./Settings";
import { getPath } from "./utils/helper";
import Navigation from "./navigation/Navigation";
import Settings from "./Settings";
import Sidebar from "./sidebar/Sidebar";
import RefreshDialog from "./RefreshDialog";
import withStorage from "./utils/withStorage";
import withRouter from "./utils/withRouter";
import eventEmitter from "./utils/eventEmitter";
import EnvBanner from "./views/envBanner/EnvBanner";
import Alert from "./Alert";

export const SIDEBAR_TYPE = {
  NORMAL: "normal",
  FULLSCREEN: "fullscreen",
  COMPACT: "compact",
  OVERLAY: "overlay",
};

let _init;

/**
 * Container component
 * @module
 * @param {element} brand - Brand element
 * @param {object} user - User
 * @param {Menu} menu - Menu
 * @param {func} onMenuFilter - On menu filter function
 * @param {elemetn} stats - Stats element
 * @param {func} onSignOut - On sign out function
 * @param {string} theme - Current theme
 * @param {array} themes - Themes
 * @param {element} header - Header element
 * @param {element} footer - Footer element
 * @param {element} userInfo - User info element
 * @param {object} uzivatel - Uzivatel
 * @param {func} applicationSettings - Application settings function
 * @param {element} manual - Manual element
 * @param {element} manualList - Manual list element
 * @param {func} handleConfirmChangePassword - Function on password change submit
 * @param {string} defaultSidebarType - Default sidebar type
 * @param {object} sideBarToggleIcons - Sidebar toggle icons
 * @param {func} navUser - Nav user
 * @param {bool} isNavBarNotScrollable - In navbar not scrollable
 * @param {bool} isPath - Show path from menu in navbar
 * @param {func} onMenuSelect - On menu select function
 * @param {array} menuList - List of menu items
 * @param {func} updateMenu - Function update menu
 */
const Container = (props) => {
  const [state, setState] = useState({
    sidebarType: props.defaultSidebarType || SIDEBAR_TYPE.NORMAL,
    isError: false,
    errorInfo: null,
    isOpenSettings: false,
    theme: { name: 'default' },
    tabsType: null,
    gridSize: null,
    page: null,
    filterable: null,
    isRefreshOpen: true,
    applicationSettings: null,
    homePage: null,
    pageList: null,
    collapsedMenu: false,
  });

  const normalSidebarClassNames = "col-3 p-0 w-sidebar";
  const overlaySidebarClassNames = "col-3 p-0 w-sidebar overBody";
  const compactSidebarClassNames = "col-1 p-0 w-sidebar-compact";
  const fullscreenSidebarClassNames = "col p-0 w-sidebar-fullscreen";

  useEffect(() => {
    if (props.provideSession) {
      props.storage.sessionProviderInit(() => {
        const afterLoginLocation =
          props.location &&
            props.location.pathname &&
            props.location.pathname !== '/prihlasenie' &&
            props.location.pathname !== '/stranka_nenajdena'
            ? props.location.pathname + props.location.search
            : '/';
        window.location.hash = '#' + afterLoginLocation;
        window.location.reload();
      });
    }
  }, [props.location, props.provideSession, props.storage]);

  const getTheme = async () => {
    let theme = await props.storage.getTheme();
    if (!theme) {
      theme = {
        name: props.theme || "default",
      };
    }
    return theme;
  };

  const getFilterable = async () => {
    let filterable = await props.storage.getFilterable();
    return filterable !== null
      ? { name: filterable ? "nie" : "áno", filterable }
      : FILTERABLE[0];
  };

  const getPathText = (route) => {
    return getPath(route, props.menu)
      .filter((x) => x.Title !== "Root")
      .map((x) => x.Title)
      .join(" > ");
  };

  const prepareSetHomePage = () => {
    let homePage = props.menuList.find((el) => el.Default);
    homePage = homePage
      ? { ...homePage, PathText: getPathText(homePage.Route) }
      : null;
    const pageList = props.menuList
      .filter(
        (el) => el.Route && el.Route !== "/" && el.Visible === 1
      )
      .map((x) => ({ ...x, PathText: getPathText(x.Route) }))
      .sort((firstEl, secondEl) =>
        firstEl.PathText > secondEl.PathText ? 1 : -1
      );
    return { homePage, pageList }
  };

  const handleOpenSettings = async () => {
    const { homePage, pageList } = prepareSetHomePage();
    const theme = await getTheme();
    const tabsType = (await props.storage.getTabsType()) || TABS_TYPE[0];
    const gridSize = (await props.storage.getGridSize()) || GRIDSIZE[1];
    const page = (await props.storage.getPage()) || PAGE[0];
    const filterable = await getFilterable();
    const applicationSettings = await props.storage.getApplicationSettings();
    setState(prev => ({
      ...prev,
      homePage,
      pageList,
      isOpenSettings: true,
      theme,
      tabsType,
      gridSize,
      page,
      filterable,
      applicationSettings,
    }));
  };

  const handleCloseSettings = () => {
    setState(prev => ({ ...prev, isOpenSettings: false }));
  };

  const handleChange = (name, value) => {
    setState(prev => ({ ...prev, [name]: value }));
  };

  const handleSidebarToggle = (event, sidebarType) => {
    setState((prevState) => ({
      ...prevState,
      sidebarType:
        prevState.sidebarType === sidebarType
          ? SIDEBAR_TYPE.NORMAL
          : sidebarType,
      collapsedMenu:
        prevState.sidebarType === sidebarType ? false : true,
    }));
  };

  const handleSidebarMenuSelect = (
    targetRoute,
    currLocation,
    routeHistory,
    isNotExpanded
  ) => {
    if (isNotExpanded) {
      if (state.sidebarType === SIDEBAR_TYPE.FULLSCREEN) {
        setState(prev => ({
          ...prev,
          sidebarType: SIDEBAR_TYPE.NORMAL,
        }));
      }
    }

    if (props.onMenuSelect) {
      props.onMenuSelect(targetRoute, currLocation, routeHistory);
    }
  };

  const evaluateSidebarClassNames = () => {
    switch (state.sidebarType) {
      case SIDEBAR_TYPE.OVERLAY:
        return overlaySidebarClassNames;
      case SIDEBAR_TYPE.COMPACT:
        return compactSidebarClassNames;
      case SIDEBAR_TYPE.FULLSCREEN:
        return fullscreenSidebarClassNames;
      default:
        return normalSidebarClassNames;
    }
  };

  const init = async () => {
    const theme = await getTheme();
    document.body.className = `theme-${theme.name}`;
    setState(prev => ({ ...prev, theme }));
  };

  const handleChangeSidebar = useCallback((type) => {
    setState(prev => ({ ...prev, sidebarType: type }));
  }, []);

  useEffect(() => {
    eventEmitter.on("CHANGE_SIDEBAR", handleChangeSidebar);
    return () => {
      eventEmitter.removeListener("CHANGE_SIDEBAR", handleChangeSidebar);
    };
  }, [handleChangeSidebar]);


  const onMouseEnter = () => {
    if (state.collapsedMenu) {
      setState(prev => ({ ...prev, collapsedMenu: false }))
    }
  }

  const onMouseLeave = () => {
    if (evaluateSidebarClassNames() !== "col-3 p-0 w-sidebar") {
      setState(prev => ({ ...prev, collapsedMenu: true }))
    }
  }

  const isAuth = props.isAuth !== null && props.isAuth !== undefined ? props.isAuth : props.user !== null;

  if (!isAuth) {
    _init = false;
  }

  if (!_init && isAuth) {
    _init = true;
    init();
  }

  const ApplicationSettings = props.applicationSettings;
  const isNavBarNotScrollable = props.isNavBarNotScrollable;
  const sideBarToggleIcons = props.sideBarToggleIcons;

  const path = props.isPath ? getPath(props.location.pathname, props.menu) : [];
  return (
    <div className="container-fluid p-0 cg-container">
      <div className="row m-0">
        {isAuth && (
          <div
            className={`${evaluateSidebarClassNames()} ${state.collapsedMenu ? "collapsed-menu" : ""}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <Sidebar
              brand={props.brand}
              onClick={(event) =>
                handleSidebarToggle(event, SIDEBAR_TYPE.COMPACT)
              }
              menu={props.menu}
              onMenuFilter={props.onMenuFilter}
              stats={props.stats}
              type={state.sidebarType}
              onMenuSelect={handleSidebarMenuSelect}
              sideBarToggleIcons={sideBarToggleIcons}
            />
          </div>
        )}
        <div
          className={`col p-0 ${isNavBarNotScrollable ? "" : "scrollable-navbar"
            } ${state.sidebarType === SIDEBAR_TYPE.FULLSCREEN ? "hidden" : ""
            } ${state.sidebarType === SIDEBAR_TYPE.OVERLAY
              ? "underSidebar"
              : ""
            }`}
          onClick={props.onClickBody}
        >
          {isAuth && (
            <div className="row m-0">
              <div className="col p-0">
                <Navigation

                  path={path.filter(x => x.Title !== "Root").map((x, id) => {
                    if (id === 0) {
                      return <span key={id}> {x.Title}</span>
                    } else {
                      return <span key={id}>
                        <i className={"k-icon k-i-arrow-chevron-right"}></i>
                        <span>{x.Title}</span>
                      </span>
                    }
                  })}
                  onSignOut={props.onSignOut}
                  onClick={(event) =>
                    handleSidebarToggle(
                      event,
                      props.useOverlaySidebarTypeOnExpand
                        ? SIDEBAR_TYPE.OVERLAY
                        : SIDEBAR_TYPE.FULLSCREEN
                    )
                  }
                  userInfo={props.userInfo}
                  handleOpenSettings={handleOpenSettings}
                  uzivatel={props.user.Uzivatel || props.uzivatel}
                  manualProps={props.manualProps}
                  getManual={props.getManual}
                  getListManual={props.getListManual}
                  getMessages={props.getMessages}
                  getNotifications={props.getNotifications}
                  navUser={props.navUser}
                  infoPanel={props.infoPanel}
                  hamburgerIcon={sideBarToggleIcons ? sideBarToggleIcons.hamburgerIcon : ""}
                />
              </div>
            </div>
          )}
          <div className="row m-0">
            <div
              className={`col p-0 ${isNavBarNotScrollable ? "scrollable-body" : ""
                }`}
            >
              {isAuth && props.header}
              <div className="row m-0" style={{ height: "100%" }}>
                <div className="col p-3">
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {props.footer}
      <Alert />
      <Settings
        handleConfirmChangePassword={props.handleConfirmChangePassword}
        isOpenSettings={state.isOpenSettings}
        handleCloseSettings={handleCloseSettings}
        handleChange={handleChange}
        theme={state.theme}
        themes={props.themes}
        filterable={state.filterable}
        page={state.page}
        tabsType={state.tabsType}
        gridSize={state.gridSize}
        applicationSettings={state.applicationSettings}
        homePage={state.homePage}
        pageList={state.pageList}
        updateMenu={props.updateMenu}
        user={props.user}
      >
        {ApplicationSettings && (
          <ApplicationSettings
            value={state.applicationSettings}
            onChange={(value) =>
              handleChange("applicationSettings", value)
            }
          />
        )}
      </Settings>
      <RefreshDialog />
      {EnvBanner()}
    </div>
  );

}

Container.propTypes = {
  brand: PropTypes.element,
  user: PropTypes.object,
  menu: PropTypes.object.isRequired,
  onMenuFilter: PropTypes.func,
  stats: PropTypes.element,
  onSignOut: PropTypes.func.isRequired,
  theme: PropTypes.string,
  themes: PropTypes.array.isRequired,
  header: PropTypes.element,
  footer: PropTypes.element,
  userInfo: PropTypes.element,
  uzivatel: PropTypes.object,
  applicationSettings: PropTypes.func,
  getManual: PropTypes.func,
  getListManual: PropTypes.func,
  getMessages: PropTypes.func,
  getNotifications: PropTypes.func,
  manual: PropTypes.element,
  manualList: PropTypes.element,
  handleConfirmChangePassword: PropTypes.func,
  defaultSidebarType: PropTypes.string,
  sideBarToggleIcons: PropTypes.object,
  navUser: PropTypes.func,
  isNavBarNotScrollable: PropTypes.bool,
  isPath: PropTypes.bool,
  onMenuSelect: PropTypes.func,
  useOverlaySidebarTypeOnExpand: PropTypes.bool,
  onClickBody: PropTypes.func,
  menuList: PropTypes.array,
  updateMenu: PropTypes.func
};

export default withRouter(withStorage(Container));
